// Pure reducer for the two-lane observe stream (foundation slices 4 + 5).
//
// The observe client receives two frame kinds over SessionChannel:
//   - kind: 'event'  → a DURABLE event { seq, type, schema_version, payload }. Canonical,
//                       ordered, deduped by seq. This is the source of truth.
//   - kind: 'delta'  → an EPHEMERAL token delta { type, payload }, NO seq. Cosmetic: it drives
//                       a live typewriter for an in-flight assistant message.
//   - kind: 'replay_batch' → an ordered backlog of DURABLE envelopes (seq > last_seq), served by
//                       SessionChannel#replay on (re)connect.
//
// AC3-of-slice-4 (reconcile, no duplicate render): the live typewriter is built by APPENDING delta
// text into a per-messageId buffer keyed off `payload.messageId`. When the DURABLE whole message
// lands (a TEXT_MESSAGE_CONTENT event for the same messageId), it REPLACES the live buffer with the
// canonical body — the delta buffer for that messageId is cleared so the message renders exactly
// once (canonical), never doubled (live + durable).
//
// AC4-of-slice-4 (replay sees only durable): deltas are broadcast-only and never persisted, so a
// replay backlog contains ONLY 'event' frames. Reducing a replay batch reconstructs the settled
// durable state with no delta involvement.
//
// ── Slice 5 additions ────────────────────────────────────────────────────────────────────────────
// AC1 (replay determinism, settled-state): `eventsBySeq` is keyed by seq, so the SETTLED durable
//   state is independent of arrival order. The client handshake buffers live frames, then requests
//   the backlog and flushes both through this one reducer; live-arrival order and replay order
//   collapse to the same `eventsBySeq` map and the same seq-sorted `durableEvents()` list. The node
//   determinism script asserts deep-equality of the two settled states.
// AC2 (reconnect / dup / gap): the reducer tracks the contiguous prefix it has APPLIED.
//   `lastAppliedSeq` is the high-water mark of the gap-free prefix; `expectedSeq()` is the next seq
//   the ordered log wants. A DUPLICATE durable frame (seq already stored, or seq <= lastAppliedSeq)
//   is dropped idempotently. A GAP (an arriving durable seq jumps past expectedSeq) sets
//   `needsResync` + `resyncFrom = lastAppliedSeq`: the client STOPS advancing and re-requests the
//   backlog from lastAppliedSeq (`replay` with last_seq = resyncFrom). Once the missing rows arrive
//   (via replay_batch or a later live frame) the contiguous prefix re-extends and the resync clears.
// AC3 (STATE_DELTA patch failure → recover from latest STATE_SNAPSHOT): foundation-spec §D (Minor)
//   DEFERS the live STATE_* runtime for v1 (the "read this event" proof carries messages + tool
//   calls, NO shared STATE), so there is no live STATE_DELTA to exercise. The recovery hook is
//   structurally present and unit-tested against synthetic STATE_* frames: the reducer remembers the
//   latest STATE_SNAPSHOT, and `recoverStateFromSnapshot(state)` resets the working state object to
//   that snapshot. This is the seam m2 plugs a real JSON-Patch runtime into; we do NOT build the
//   full patch state machine here.
//
// State shape:
//   {
//     eventsBySeq:    { [seq]: envelope },   // deduped durable log (settled state, order-free)
//     liveDeltas:     { [messageId]: string },
//     maxSeq:         number,                // highest seq SEEN (for backlog high-water on connect)
//     lastAppliedSeq: number,               // highest seq of the contiguous gap-free APPLIED prefix
//     needsResync:    boolean,              // a gap was observed; client should re-request backlog
//     resyncFrom:     number,               // last_seq to replay from when resyncing (= lastAppliedSeq)
//     stateSnapshot:  object | null,        // latest STATE_SNAPSHOT payload (recovery anchor)
//     workingState:   object | null,        // current shared STATE (deferred-live per §D Minor)
//   }

export const initialObserveState = () => ({
  eventsBySeq: {},
  liveDeltas: {},
  maxSeq: 0,
  lastAppliedSeq: 0,
  needsResync: false,
  resyncFrom: 0,
  stateSnapshot: null,
  workingState: null,
})

// Pull the messageId a TEXT_MESSAGE_* event/delta belongs to (payload.messageId is the AG-UI key).
const messageIdOf = (frameEvent) => frameEvent && frameEvent.payload && frameEvent.payload.messageId

// A run-terminal durable event. After one of these, no live typewriter cursor should linger.
const TERMINAL_TYPES = new Set(["RUN_FINISHED", "RUN_ERROR"])

// Durable event types that carry/close a canonical assistant body and so supersede the matching live
// typewriter lane (A2 / #11). TEXT_MESSAGE_CONTENT is the whole-body event; TEXT_MESSAGE_END closes
// the streaming trio for turns that land durable via the trio close rather than a single content row.
const DURABLE_RECONCILE_TYPES = new Set(["TEXT_MESSAGE_CONTENT", "TEXT_MESSAGE_END"])

// The seq of the most recent RUN_STARTED in the durable log (0 if none) — the lower boundary of the
// CURRENT run. Durable assistant text from PRIOR runs must NOT satisfy the "only copy" guard for the
// run that is settling now (that session-wide leak is the #3 turn-2+ disappear — see below).
const latestRunStartedSeq = (eventsBySeq) => {
  let max = 0
  for (const seq in eventsBySeq) {
    const ev = eventsBySeq[seq]
    if (ev && ev.type === "RUN_STARTED" && ev.seq > max) max = ev.seq
  }
  return max
}

// Does the deduped durable log carry canonical assistant text WITHIN the current run (seq strictly
// after `sinceSeq`, the latest RUN_STARTED)? If so, a stuck live typewriter for THIS run (one whose
// messageId never matched a durable body) is a pure DUPLICATE of an already-rendered turn — safe to
// drop. If NOT, the typewriter is the only copy of this run's reply and must be kept.
//
// RUN-SCOPED (#3 fix): the pre-fix scan was session-wide, so from turn 2 on a prior turn's durable
// text always satisfied the guard → reconcileStuckTypewriter wiped a live-ONLY reply on RUN_FINISHED
// → the turn-2+ disappear. Scoping to the current run restores the original "never erase the only
// copy" intent for EVERY turn, not just turn 1. `sinceSeq=0` (no RUN_STARTED seen) preserves the
// legacy session-wide behavior as a safe fallback.
const hasDurableAssistantText = (eventsBySeq, sinceSeq = 0) => {
  for (const seq in eventsBySeq) {
    const ev = eventsBySeq[seq]
    if (ev && ev.type === "TEXT_MESSAGE_CONTENT" && ev.seq > sinceSeq) return true
  }
  return false
}

// The next seq the ordered durable log expects after the contiguous applied prefix.
export const expectedSeq = (state) => state.lastAppliedSeq + 1

// Walk the contiguous prefix forward from lastAppliedSeq: as long as the next expected seq is
// present in eventsBySeq, advance lastAppliedSeq. This both APPLIES newly-arrived in-order rows and
// HEALS a gap once the missing rows are backfilled (clearing needsResync when the prefix is whole).
const advancePrefix = (state) => {
  let lastAppliedSeq = state.lastAppliedSeq
  while (state.eventsBySeq[lastAppliedSeq + 1] != null) lastAppliedSeq += 1

  // Resync stays asserted only while a true hole remains: the prefix has not yet reached maxSeq.
  const needsResync = lastAppliedSeq < state.maxSeq
  return {
    ...state,
    lastAppliedSeq,
    needsResync,
    resyncFrom: needsResync ? lastAppliedSeq : 0,
  }
}

// Apply one durable envelope. Store by seq (dedupe — a re-delivered seq is a no-op for the settled
// map), reconcile any live typewriter buffer, then re-walk the contiguous prefix (which advances the
// applied high-water mark and detects/heals gaps).
//
// `fromReplay` (job-adoption fix): true when this envelope arrived inside a `replay_batch` rather
// than a live `event` frame. See the RUN_STARTED handling below for why it matters.
const applyDurable = (state, envelope, fromReplay = false) => {
  if (envelope == null || envelope.seq == null) return state

  // Duplicate: this seq is already in the deduped log → drop it (idempotent), but still re-walk the
  // prefix in case earlier processing left a gap that this (re)delivery does not change.
  const isDuplicate = state.eventsBySeq[envelope.seq] != null

  // A2 (single live lane, #11): the canonical durable body supersedes the live typewriter buffer.
  // It can land as TEXT_MESSAGE_CONTENT (the whole-body event) OR — for turns that settle via the
  // streaming-trio close — as TEXT_MESSAGE_END. Reconcile (clear the live lane) on EITHER, keyed by
  // the envelope's messageId (reads the inner `envelope.type` axis), so only one lane ever renders.
  let liveDeltas = state.liveDeltas
  if (!isDuplicate && DURABLE_RECONCILE_TYPES.has(envelope.type)) {
    const mid = messageIdOf(envelope)
    if (mid != null && mid in liveDeltas) {
      liveDeltas = { ...liveDeltas }
      delete liveDeltas[mid] // canonical durable body supersedes the live typewriter buffer
    }
  }

  // A3 (per-turn live reset, #3 enabling condition): a fresh RUN_STARTED opens a new turn. Clear any
  // stale ephemeral typewriter buffer (preserve eventsBySeq — the durable log is the source of
  // truth). A duplicate RUN_STARTED (re-delivered seq) must NOT wipe a live lane mid-stream.
  //
  // JOB-ADOPTION FIX: only a LIVE RUN_STARTED sweeps stale live buffers. A REPLAYED RUN_STARTED
  // (fromReplay) can never legitimately need to — replay_batch never carries deltas (AC4), so the
  // only liveDeltas that can exist when a replayed RUN_STARTED lands are (a) a stale orphan from a
  // session that predates this handshake (harmless to leave — the next LIVE RUN_STARTED will still
  // sweep it, per the untouched two-turn-survive invariant) or (b) the CURRENTLY-ADOPTING run's own
  // in-flight reply, which raced ahead of its own (chronologically earlier, but late-arriving) durable
  // RUN_STARTED. Case (b) must never be wiped — it is the only copy of the reply in flight right now.
  if (!isDuplicate && envelope.type === "RUN_STARTED" && !fromReplay) {
    liveDeltas = {}
  }

  const eventsBySeq = isDuplicate
    ? state.eventsBySeq
    : { ...state.eventsBySeq, [envelope.seq]: envelope }
  const maxSeq = envelope.seq > state.maxSeq ? envelope.seq : state.maxSeq

  let next = { ...state, eventsBySeq, liveDeltas, maxSeq }
  next = applyStateEvent(next, envelope) // STATE_* recovery seam (deferred-live per §D Minor)
  next = advancePrefix(next)
  return reconcileStuckTypewriter(next)
}

// Belt-and-suspenders typewriter reconciliation. The normal path clears liveDeltas[mid] the instant
// a durable TEXT_MESSAGE_CONTENT with the SAME messageId lands (applyDurable above). But if the two
// lanes ever key the same text under DIFFERENT messageIds (the ephemeral/durable mismatch this
// change targets — see translate.ts), that delete never fires and the typewriter sticks forever:
// literal markdown + a cursor that never clears, even though the run finished (Symptom A).
//
// So once the stream has SETTLED ON A TERMINAL — the highest-seq durable event is a run-end (no
// newer run is in flight) AND the contiguous prefix is whole (no gap → every event for that run is
// present and rendered) — drop any leftover typewriter. Keying on the terminal being the HEAD (not
// merely on applying a terminal frame) also covers the reconnect edge where the terminal arrives
// during a gap and only a later backfill heals the prefix: this re-runs on that backfill.
//
// Guarded so we NEVER erase the only copy of a reply: if no durable assistant text exists yet, the
// typewriter IS the reply (Symptom B) — keep it.
const reconcileStuckTypewriter = (state) => {
  if (state.needsResync) return state // a gap remains → not settled; the body may be in the hole
  if (Object.keys(state.liveDeltas).length === 0) return state // nothing stuck
  const head = state.eventsBySeq[state.maxSeq]
  if (head == null || !TERMINAL_TYPES.has(head.type)) return state // a run is still in flight
  // Run-scoped: only THIS run's durable text proves the stuck lane is a duplicate. Prior turns'
  // durable text must not authorize wiping a live-only reply (the #3 turn-2+ disappear).
  const runStart = latestRunStartedSeq(state.eventsBySeq)
  if (!hasDurableAssistantText(state.eventsBySeq, runStart)) return state // no canonical body THIS run → keep
  return { ...state, liveDeltas: {} }
}

// STATE_SNAPSHOT / STATE_DELTA recovery seam. Structurally present, deferred-live per spec §D Minor:
// v1 emits no STATE_DELTA, so there is no live patch to fail. We record the latest snapshot so the
// recovery hook has an anchor, and apply a delta defensively (a malformed/failed patch falls back to
// the latest snapshot rather than corrupting workingState).
const applyStateEvent = (state, envelope) => {
  if (envelope.type === "STATE_SNAPSHOT") {
    const snapshot = (envelope.payload && envelope.payload.snapshot) || {}
    return { ...state, stateSnapshot: snapshot, workingState: snapshot }
  }
  if (envelope.type === "STATE_DELTA") {
    try {
      const patched = applyStateDelta(state.workingState, envelope.payload && envelope.payload.delta)
      if (patched == null) throw new Error("delta produced no state")
      return { ...state, workingState: patched }
    } catch (_e) {
      // Patch failure → recover from the latest STATE_SNAPSHOT (the contract guarantee).
      return recoverStateFromSnapshot(state)
    }
  }
  return state
}

// Minimal defensive delta application (NOT a full JSON-Patch machine — m2 plugs the real one in
// here). v1 never exercises this live; it exists so a failure path can be unit-asserted.
const applyStateDelta = (workingState, delta) => {
  if (delta == null) return null // no delta → signal failure → snapshot recovery
  return { ...(workingState || {}), ...delta }
}

// Public recovery hook (AC3): reset the working state to the latest STATE_SNAPSHOT. With no snapshot
// yet seen, working state is cleared to null (nothing to recover to).
export const recoverStateFromSnapshot = (state) => ({
  ...state,
  workingState: state.stateSnapshot != null ? { ...state.stateSnapshot } : null,
})

// Apply one ephemeral delta: APPEND its text into the per-messageId live buffer. Never touches
// eventsBySeq, maxSeq, or the applied prefix (a delta has no seq and is never part of the durable log).
const applyDelta = (state, deltaEvent) => {
  if (deltaEvent == null || deltaEvent.type !== "TEXT_MESSAGE_CONTENT") return state
  const mid = messageIdOf(deltaEvent)
  if (mid == null) return state
  const text = (deltaEvent.payload && deltaEvent.payload.delta) || ""
  return {
    ...state,
    liveDeltas: { ...state.liveDeltas, [mid]: (state.liveDeltas[mid] || "") + text },
  }
}

// Reduce ONE channel frame. `{ kind: 'event', event }` (durable), `{ kind: 'delta', event }`
// (ephemeral), or `{ kind: 'replay_batch', events }` (durable backlog). Unknown kinds pass through.
export const observeReducer = (state, frame) => {
  if (frame == null) return state
  switch (frame.kind) {
    case "event":
      return applyDurable(state, frame.event)
    case "replay_batch":
      return (frame.events || []).reduce((s, env) => applyDurable(s, env, true), state)
    case "delta":
      return applyDelta(state, frame.event)
    // A3 (#3 / RC-6, shared with Wave 4): an explicit reset dispatch verb (the OUTER frame.kind axis).
    //   scope:'chat' → switching chats: wipe ALL prior durable+live state (a clean new subscription).
    //   scope:'turn' → clear the ephemeral live lane only, preserving the durable log.
    // Dispatched as { kind:'reset', scope }, NEVER { type:'reset' } (which would fall to default).
    case "reset":
      return frame.scope === "chat"
        ? initialObserveState()
        : { ...state, liveDeltas: {} }
    default:
      return state
  }
}

// Selector: the ordered durable events (the canonical, deduped, seq-sorted list). This is the
// SETTLED state asserted for replay determinism — order-free because it is rebuilt from the seq map.
export const durableEvents = (state) =>
  Object.values(state.eventsBySeq).sort((a, b) => a.seq - b.seq)
